import * as yup from 'yup';
import moment from 'moment';
import { PersonalInfoDTO } from './api/usePersonalInfoQuery';
import {
  DependentsFormValues,
  dependentsValidation,
} from './components/Dependents/Dependents.meta';
import { principalValidation, YourInfoFormValues } from './components/YourInfo/YourInfo.meta';
import {
  PartnerInfoFormValues,
  partnerValidation,
} from './components/PartnerInfo/PartnerInfo.meta';
import {
  EstatePlanningDTO,
  EstatePlanningOptionsDTO,
} from './api/useEstatePlanningPersonalInfoQuery';
import {
  DeductionsAndGiftsValues,
  deductionsValidation,
} from './components/Deductions/Deductions.meta';

export const FORM_ID = 'form--personal-info';

export type FormValues = {
  personalInfo: YourInfoFormValues & PartnerInfoFormValues & DependentsFormValues;
  deductionsAndGifts?: DeductionsAndGiftsValues;
};

const formPersonalInfo = (
  data: DataToFormParam,
): YourInfoFormValues & PartnerInfoFormValues & DependentsFormValues => {
  return {
    principal: {
      firstName: data.PersonalInfo.Principal.FirstName,
      lastName: data.PersonalInfo.Principal.LastName,
      dob: data.PersonalInfo.Principal.Dob
        ? moment.utc(data.PersonalInfo.Principal.Dob).format('MM/DD/YYYY')
        : '',
    },

    partner: {
      firstName: data.PersonalInfo.Partner?.FirstName || '',
      lastName: data.PersonalInfo.Partner?.LastName || '',
      dob: data.PersonalInfo.Partner?.Dob
        ? moment.utc(data.PersonalInfo.Partner.Dob).format('MM/DD/YYYY')
        : '',
    },

    spouseTaxFilingState: data.EstatePlanning?.SpouseTaxFilingState || null,

    filingState: data.PersonalInfo.Taxes.FilingState,
    filingStatus: data.PersonalInfo.Taxes.FilingStatus,

    dependents:
      data.PersonalInfo.Dependents?.length > 0
        ? data.PersonalInfo.Dependents.map(dependent => ({
            id: dependent.Id,
            firstName: dependent.FirstName || '',
            yearOfBirth: dependent.Dob ? moment.utc(dependent.Dob).format('YYYY') : '',
            type: dependent.Type,
            isDependent: !!dependent.IsDependent,
          }))
        : [],
  };
};

interface DataToFormParam {
  PersonalInfo: PersonalInfoDTO;
  EstatePlanning?: EstatePlanningDTO;
  EstatePlanningOptions?: EstatePlanningOptionsDTO;
}

export const dataToForm = (data: DataToFormParam): FormValues => {
  const personalInfo = formPersonalInfo(data);

  if (!data.EstatePlanningOptions) {
    return {
      personalInfo,
    };
  }

  return {
    personalInfo,

    deductionsAndGifts: {
      principal: {
        lifetimeGiftingExemptionUsedAmount:
          data.EstatePlanning?.DeductionsAndGifts.PrincipalDeductionsAndGifts
            .LifetimeGiftingExemptionUsedAmount || 0,
        administrationExpenseDeductionAmount:
          data.EstatePlanning?.DeductionsAndGifts.PrincipalDeductionsAndGifts
            .AdministrationExpenseDeductionAmount || 0,
      },
      spouse: {
        lifetimeGiftingExemptionUsedAmount:
          data.EstatePlanning?.DeductionsAndGifts.SpouseDeductionsAndGifts
            ?.LifetimeGiftingExemptionUsedAmount || 0,
        administrationExpenseDeductionAmount:
          data.EstatePlanning?.DeductionsAndGifts.SpouseDeductionsAndGifts
            ?.AdministrationExpenseDeductionAmount || 0,
      },
    },
  };
};

interface ResetForm {
  PersonalInfo: PersonalInfoDTO;
  EstatePlanning: EstatePlanningDTO;
}

export const resetForm = (data: ResetForm): FormValues => {
  return {
    personalInfo: formPersonalInfo(data),

    deductionsAndGifts: {
      principal: {
        lifetimeGiftingExemptionUsedAmount:
          data.EstatePlanning.DeductionsAndGifts.PrincipalDeductionsAndGifts
            .LifetimeGiftingExemptionUsedAmount,
        administrationExpenseDeductionAmount:
          data.EstatePlanning.DeductionsAndGifts.PrincipalDeductionsAndGifts
            .AdministrationExpenseDeductionAmount,
      },
      spouse: {
        lifetimeGiftingExemptionUsedAmount:
          data.EstatePlanning.DeductionsAndGifts.SpouseDeductionsAndGifts
            ?.LifetimeGiftingExemptionUsedAmount,
        administrationExpenseDeductionAmount:
          data.EstatePlanning.DeductionsAndGifts.SpouseDeductionsAndGifts
            ?.AdministrationExpenseDeductionAmount,
      },
    },
  };
};

export const validation = yup.object().shape({
  personalInfo: yup.object().shape({
    principal: principalValidation,
    partner: partnerValidation,
    dependents: dependentsValidation,
  }),

  deductionsAndGifts: yup.object().when('$showEstatePlanning', {
    is: true,
    then: deductionsValidation,
  }),
});

export enum PersonalInfoContext {
  CharitableGivingWorkflow,
}
